import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import { Globe, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { getStatusStyle, CHART_SERIES } from '../../config/statusColors';
import { formatCurrency } from '../../lib/fixedIncome';
import { FX_HEDGES } from '../../data/portfolioMockData';

// Currency to Country lookup for hedge-ratio matching
const CURRENCY_COUNTRY = {
  KES: 'Kenya',
  NGN: 'Nigeria',
  GHS: 'Ghana',
  RWF: 'Rwanda',
  UGX: 'Uganda',
  TZS: 'Tanzania',
  ZAR: 'South Africa',
  EGP: 'Egypt',
};

const FxOverlayTab = ({ positions, role, fxHedges: fxHedgesProp }) => {
  const hedgeData = fxHedgesProp || FX_HEDGES;

  // Filter hedges by role
  const filteredHedges = useMemo(() => {
    if (role === 'investor_admin') return hedgeData;
    if (role === 'investor_analyst') return hedgeData.filter((h) => h.entity === 'ENT-003');
    return hedgeData;
  }, [role, hedgeData]);

  // Aggregate P&L
  const totalHedgedNotional = useMemo(
    () => filteredHedges.reduce((sum, h) => sum + h.notionalUsd, 0),
    [filteredHedges],
  );

  const unrealizedPnl = useMemo(
    () => filteredHedges.reduce((sum, h) => sum + h.mtmPnl, 0),
    [filteredHedges],
  );

  const _realizedPnl = 0; // eslint-disable-line no-unused-vars -- placeholder for future realized hedge P&L

  // Net currency exposure
  const currencyExposure = useMemo(() => {
    const grossMap = {};
    (positions || []).forEach((p) => {
      const ccy = p.currency;
      if (!grossMap[ccy]) grossMap[ccy] = 0;
      grossMap[ccy] += (p.faceValue || 0) / (p.currentFxRate || 1);
    });
    const hedgeMap = {};
    filteredHedges.forEach((h) => {
      const ccy = h.currencyPair.split('/')[0];
      if (!hedgeMap[ccy]) hedgeMap[ccy] = 0;
      hedgeMap[ccy] += h.notionalUsd;
    });
    const allCurrencies = new Set([...Object.keys(grossMap), ...Object.keys(hedgeMap)]);
    return Array.from(allCurrencies)
      .map((ccy) => {
        const gross = grossMap[ccy] || 0;
        const hedged = hedgeMap[ccy] || 0;
        return { currency: ccy, grossExposure: gross, hedgedAmount: hedged, netExposure: Math.max(gross - hedged, 0) };
      })
      .filter((d) => d.grossExposure > 0)
      .sort((a, b) => b.netExposure - a.netExposure);
  }, [positions, filteredHedges]);

  // Hedge ratio by market (country)
  const hedgeRatioData = useMemo(() => {
    const exposureByCountry = {};
    (positions || []).forEach((p) => {
      const country = p.country;
      if (!exposureByCountry[country]) exposureByCountry[country] = { usd: 0, currency: p.currency };
      exposureByCountry[country].usd += (p.faceValue || 0) / (p.currentFxRate || 1);
    });
    const hedgedByCountry = {};
    filteredHedges.forEach((h) => {
      const ccy = h.currencyPair.split('/')[0];
      const country = CURRENCY_COUNTRY[ccy];
      if (country) {
        if (!hedgedByCountry[country]) hedgedByCountry[country] = 0;
        hedgedByCountry[country] += h.notionalUsd;
      }
    });
    return Object.entries(exposureByCountry)
      .map(([country, data]) => {
        const hedged = hedgedByCountry[country] || 0;
        const ratio = data.usd > 0 ? Math.min(hedged / data.usd, 1) : 0;
        return { country, hedgeRatio: ratio, exposureUsd: data.usd };
      })
      .sort((a, b) => b.exposureUsd - a.exposureUsd);
  }, [positions, filteredHedges]);

  // Summary cards config
  const summaryCards = [
    { title: 'Total Hedged Notional', value: formatCurrency(totalHedgedNotional), Icon: Globe },
    {
      title: 'Unrealized Foreign Exchange P&L',
      value: (unrealizedPnl >= 0 ? '+' : '-') + formatCurrency(Math.abs(unrealizedPnl)),
      color: unrealizedPnl >= 0 ? 'text-emerald-600' : 'text-red-600',
      Icon: unrealizedPnl >= 0 ? TrendingUp : TrendingDown,
    },
    { title: 'Realized Foreign Exchange P&L', value: '$0', Icon: DollarSign },
    { title: 'Active Hedges', value: filteredHedges.length, Icon: Globe },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                  {card.title}
                </p>
                <p className={"text-2xl font-bold font-sans mt-1 " + (card.color || 'text-black')}>
                  {card.value}
                </p>
              </div>
              <div className="p-3 border border-zinc-300 bg-zinc-50 rounded-none">
                <card.Icon className="w-5 h-5 text-black" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Foreign Exchange Hedge Positions Table */}
      <div className="bg-white rounded-none border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="text-sm font-semibold text-black">Active Foreign Exchange Hedge Positions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider">Currency Pair</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider">Direction</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">Notional (Local)</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">Notional (USD)</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">Forward Rate</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">Spot at Inception</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">Current Spot</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider text-right">MTM P&L</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider">Maturity</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredHedges.map((h) => {
                const statusStyle = getStatusStyle(h.status);
                const pnlPositive = h.mtmPnl >= 0;
                return (
                  <tr key={h.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{h.id}</td>
                    <td className="px-4 py-3 font-medium">{h.currencyPair}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-sm bg-red-50 text-red-700">Sell</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{h.notional.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{formatCurrency(h.notionalUsd)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{h.forwardRate.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{h.spotRateAtInception.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{h.currentSpot.toFixed(2)}</td>
                    <td className={"px-4 py-3 text-right font-mono text-xs font-medium " + (pnlPositive ? 'text-emerald-600' : 'text-red-600')}>
                      {pnlPositive ? '+' : ''}{formatCurrency(h.mtmPnl)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {new Date(h.maturityDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-sm capitalize" style={statusStyle}>{h.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts: Donut + Bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net Currency Exposure Donut */}
        <div className="bg-white rounded-none border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="text-sm font-semibold text-black">Net Currency Exposure (USD)</h3>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-6">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={currencyExposure}
                      dataKey="netExposure"
                      nameKey="currency"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                    >
                      {currencyExposure.map((entry, idx) => (
                        <Cell key={entry.currency} fill={CHART_SERIES[idx % CHART_SERIES.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ fontSize: '12px', borderRadius: 0, border: '1px solid #e4e4e7' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {currencyExposure.map((entry, idx) => (
                  <div key={entry.currency} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_SERIES[idx % CHART_SERIES.length] }} />
                      <span className="font-medium text-zinc-700">{entry.currency}</span>
                    </div>
                    <span className="font-mono text-zinc-500">{formatCurrency(entry.netExposure)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Hedge Ratio by Market Bar Chart */}
        <div className="bg-white rounded-none border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="text-sm font-semibold text-black">Hedge Ratio by Market</h3>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={hedgeRatioData.map((d) => ({
                  ...d,
                  hedgeRatioPct: Math.round(d.hedgeRatio * 100),
                }))}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="country"
                  tick={{ fontSize: 11, fill: '#71717a' }}
                  axisLine={{ stroke: '#e4e4e7' }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: '#71717a' }}
                  tickFormatter={(v) => `${v}%`}
                  axisLine={{ stroke: '#e4e4e7' }}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Hedge Ratio']}
                  contentStyle={{ fontSize: '12px', borderRadius: 0, border: '1px solid #e4e4e7' }}
                />
                <ReferenceLine
                  y={50}
                  stroke="#71717a"
                  strokeDasharray="4 4"
                  label={{
                    value: 'Min Target',
                    position: 'right',
                    fontSize: 10,
                    fill: '#71717a',
                  }}
                />
                <Bar dataKey="hedgeRatioPct" fill={CHART_SERIES[1]} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FxOverlayTab;
